import { StyledOption } from '@bambi/component-library/src/components/inputs/Combobox/StyledOption';
import type { SelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';
import { useCallback } from 'react';
import type { FieldValues, Path } from 'react-hook-form';

import type {
  PaginatedPassengerAddressListRead,
  PassengerRead,
  PayerOrganizationRead,
} from 'src/common/external/bambi-api/bambiApi';
import { usePassengersRecentAddressesListQuery } from 'src/common/external/bambi-api/bambiApi';
import getLocationOptionAdornment from 'src/features/add-trip/TripDetails/getLocationOptionAdornment';
import { useAdditionalResultsQuery } from 'src/features/add-trip/TripDetails/useAdditionalResultsQuery';

export interface UseAddressDataProps {
  selectedPassenger: PassengerRead | null | undefined;
  selectedPayer: PayerOrganizationRead | null | undefined;
  skipRecentAddresses?: boolean;
}

/**
 * Responsible for fetching recent addresses and additional results
 * @param selectedPassenger - The selected passenger
 * @param selectedPayer - The selected payer
 * @param skipRecentAddresses - Whether to skip fetching recent addresses
 * @returns The recent address response and the additional results query function
 */
export function useAddressData({
  selectedPassenger,
  selectedPayer,
  skipRecentAddresses = false,
}: UseAddressDataProps) {
  const { data: recentAddressResponse } = usePassengersRecentAddressesListQuery(
    {
      id: selectedPassenger?.id || '',
    },
    {
      skip: !selectedPassenger?.id || skipRecentAddresses,
    }
  );

  const additionalResultsQueryFn = useAdditionalResultsQuery(
    recentAddressResponse,
    selectedPayer
  );

  return {
    recentAddressResponse,
    additionalResultsQueryFn,
  };
}

export interface UseAddressAutoFillProps<T extends FieldValues> {
  recentAddressResponse: PaginatedPassengerAddressListRead | undefined;
  setValue: (fieldName: Path<T>, value: string) => void;
  addressDetailsFieldName: Path<T>;
  driverNotesFieldName: Path<T>;
}

interface ChangeableField {
  onChange: (args: { value: string; label: string }) => void;
}

/**
 * Responsible for auto-filling address details from recent addresses
 * @param recentAddressResponse - The recent address response
 * @param setValue - The setValue function
 * @param addressDetailsFieldName - The field name for the address details
 * @param driverNotesFieldName - The field name for the driver notes
 */
export function useAddressAutoFill<T extends FieldValues>({
  recentAddressResponse,
  setValue,
  addressDetailsFieldName,
  driverNotesFieldName,
}: UseAddressAutoFillProps<T>) {
  const handleLocationChange = useCallback(
    (location: SelectOption, field: ChangeableField) => {
      // Find matching recent address
      const recentAddress = recentAddressResponse?.results.find(
        (result) => result.address.location?.place_id === location.value
      );

      // Auto-fill address details and notes if recent address found
      if (recentAddress) {
        setValue(addressDetailsFieldName, recentAddress.address_details || '');
        setValue(driverNotesFieldName, recentAddress.address_notes || '');
      }

      // Update the main field value
      field.onChange({
        value: location.value,
        label: location.label,
      });
    },
    [
      recentAddressResponse,
      setValue,
      addressDetailsFieldName,
      driverNotesFieldName,
    ]
  );

  return { handleLocationChange };
}

/**
 * Responsible for rendering location options with adornments
 * @param recentAddressResponse - The recent address response
 * @returns The renderOption function
 */
export function useAddressOptionRenderer(
  recentAddressResponse: PaginatedPassengerAddressListRead | undefined
) {
  const renderOption = useCallback(
    (option: SelectOption, selected: boolean, active: boolean) => {
      const adornment = getLocationOptionAdornment({
        option,
        recentAddressResponse,
      });

      return (
        <StyledOption
          key={option.value}
          option={option}
          selected={selected}
          active={active}
          startAddornment={adornment}
        />
      );
    },
    [recentAddressResponse]
  );

  return { renderOption };
}
